/**
 * Platform Detection Service
 * Detects whether the app is running as PWA, Electron, or Capacitor
 */

export type Platform = 'pwa' | 'electron' | 'capacitor';

declare global {
  interface Window {
    __ELECTRON__?: boolean;
    __ELECTRON_PLATFORM__?: string;
    electronAPI?: {
      send: (channel: string, ...args: unknown[]) => void;
      on: (channel: string, callback: (...args: unknown[]) => void) => (() => void) | undefined;
      invoke: (channel: string, ...args: unknown[]) => Promise<unknown>;
    };
    Capacitor?: {
      isNativePlatform: () => boolean;
      getPlatform: () => string;
    };
  }
}

let detectedPlatform: Platform | null = null;

export function detectPlatform(): Platform {
  if (detectedPlatform) return detectedPlatform;

  if (typeof window !== 'undefined' && window.__ELECTRON__) {
    detectedPlatform = 'electron';
  } else if (typeof window !== 'undefined' && window.Capacitor?.isNativePlatform()) {
    detectedPlatform = 'capacitor';
  } else {
    detectedPlatform = 'pwa';
  }

  return detectedPlatform;
}

export function isElectron(): boolean {
  return detectPlatform() === 'electron';
}

export function isCapacitor(): boolean {
  return detectPlatform() === 'capacitor';
}

export function isPWA(): boolean {
  return detectPlatform() === 'pwa';
}

export function isMacOS(): boolean {
  return isElectron() && window.__ELECTRON_PLATFORM__ === 'darwin';
}

export function isWindows(): boolean {
  return isElectron() && window.__ELECTRON_PLATFORM__ === 'win32';
}

export function getPlatformName(): string {
  const platform = detectPlatform();
  switch (platform) {
    case 'electron': return 'Desktop';
    case 'capacitor': return 'Mobile';
    case 'pwa': return 'Web';
  }
}
